// DATA TRUST F5 — the freshness envelope on screen. ONE chip component for every headline number
// and chart: `<Freshness meta/>` reads the `meta` block every numeric payload now carries
// ({as_of, age_sessions, stale, stale_reason, partial, gaps, release}) and renders
//   "as of 2026-06-12 · 3 sessions"   (neutral)       — fresh, dated
//   "as of 2026-06-12 · 91 sessions"  (amber + reason) — stale: the reason rides in the tooltip
//   "no as-of"                        (amber)          — the payload carries no date
// `<Verdict value reason/>` renders a dependent verdict (on plan, nothing to do, reconciled): green
// when true, amber when false, GREY "unverified" + reason when null (the backend nulls it on stale
// input — invariant 10: never green on stale data). `<DataHealthChip/>` is the top-bar status from
// /api/health/data (red = run overdue or gate failed, amber = something stale, ok otherwise).
// Chips, not banners (UI concision); the caveat sits next to the number (caveat-transparency).
import { useEffect, useState } from "react";
import { api } from "../api.js";
import { yearFrac } from "../dates.js";

export const ageLabel = (m) => (!m || m.age_sessions == null ? "" : m.age_sessions === 0 ? "today" : m.age_sessions === 1 ? "1 session" : `${m.age_sessions} sessions`);

export function Freshness({ meta, what = "", size = "sm", testid = "freshness-chip" }) {
  if (!meta) return null;
  const stale = !!meta.stale;
  const label = meta.as_of ? `as of ${meta.as_of}${meta.age_sessions != null ? ` · ${ageLabel(meta)}` : ""}` : "no as-of";
  const extras = [meta.partial ? "last period partial" : null, meta.gaps?.length ? `${meta.gaps.length} gap${meta.gaps.length === 1 ? "" : "s"}` : null].filter(Boolean);
  const title = [what, stale ? `STALE — ${meta.stale_reason || "no as-of"}` : `fresh (${label})`, ...extras,
    meta.source ? `source ${meta.source}` : null, meta.release ? `release ${meta.release}` : null].filter(Boolean).join(" · ");
  return (
    <span className={`chip chip--fresh chip--fresh-${size} ${stale ? "chip--stale" : ""}`} title={title}
      data-testid={testid} data-stale={stale ? "1" : "0"}>
      {stale ? "⚠ " : ""}{label}{extras.length ? ` · ${extras.join(" · ")}` : ""}
    </span>
  );
}

export function Verdict({ value, reason, ok = "ok", bad = "attention", unverified = "unverified", testid = "verdict-chip", className = "" }) {
  if (value === true) return <span className={`chip chip--ok ${className}`} data-testid={testid} data-verdict="true">{ok}</span>;
  if (value === false) return <span className={`chip chip--warn ${className}`} data-testid={testid} data-verdict="false">{bad}</span>;
  return <span className={`chip chip--unverified ${className}`} data-testid={testid} data-verdict="null"
    title={reason ? `unverifiable — ${reason}` : "unverifiable — an input is stale or missing"}>{unverified}</span>;
}

// Top-bar data-health chip. Fetches once per mount (+ every 10 min); silent on error (the chip is a
// courtesy, never a blocker). Click → Automation (admins) where the per-artifact table lives.
export function DataHealthChip() {
  const [h, setH] = useState(null);
  useEffect(() => {
    let alive = true;
    const load = () => api("health/data").then((r) => alive && setH(r)).catch(() => alive && setH({ status: "unknown", reasons: ["health read failed"] }));
    load();
    const id = setInterval(load, 10 * 60 * 1000);
    return () => { alive = false; clearInterval(id); };
  }, []);
  if (!h) return null;
  const cls = h.status === "ok" ? "chip--ok" : h.status === "red" ? "chip--bad" : h.status === "amber" ? "chip--warn" : "chip--unverified";
  const text = h.status === "ok" ? "data ok" : h.status === "red" ? "data red" : h.status === "amber" ? "data stale" : "data ?";
  const title = [
    ...(h.reasons || []),
    h.last_run?.finished ? `last run ${h.last_run.finished} (${h.last_run.age_hours} h ago)` : null,
    h.oldest_artifact ? `oldest artifact ${h.oldest_artifact.name} as of ${h.oldest_artifact.as_of}` : null,
  ].filter(Boolean).join(" · ") || "every served artifact is fresh and the last run was on schedule";
  // The desktop top bar is at capacity at 1440px (the UI audit's width), so the chip is a 14px status
  // DOT with a glyph (never colour alone): "·" ok, "!" amber/red, "?" unknown; the text lives in the
  // title/aria-label and in full on Automation. `data-status` + the label carry the state for tests/AT.
  const glyph = h.status === "ok" ? "·" : h.status === "unknown" ? "?" : "!";
  return <span className={`health-dot health-dot--${h.status}`} role="status" data-testid="data-health-chip" data-status={h.status}
    aria-label={`${text} — ${title}`} title={`${text} — ${title}`}>{glyph}</span>;
}

// ── chart helpers ────────────────────────────────────────────────────────────────────────────
// ONE gap rule app-wide (mirrors api/freshness.gaps_in): a hole is > GAP_SESSIONS weekday sessions
// between consecutive points. ponytail: weekday calendar — a market holiday is one session of slack,
// which is why the threshold is not "Δ > 1 trading day" literally (that would flag every holiday as
// "no data"); upgrade path = an exchange calendar. A downsampled head (strided history) must NOT be
// gap-scanned client-side — use the backend's meta.gaps (computed on the full series) for those.
export const GAP_SESSIONS = 3;
const DAY = 86400000;
const utc = (d) => new Date(String(d).slice(0, 10) + "T00:00:00Z");
export const sessionsBetween = (a, b) => {          // weekdays in (a, b]; 0 when b ≤ a
  let n = 0, t = utc(a).getTime(); const end = utc(b).getTime();
  while (t < end) { t += DAY; const wd = new Date(t).getUTCDay(); if (wd !== 0 && wd !== 6) n++; }
  return n;
};
export const nextSession = (d) => {                  // the next weekday after d (ISO date)
  let t = utc(d).getTime() + DAY;
  while ([0, 6].includes(new Date(t).getUTCDay())) t += DAY;
  return new Date(t).toISOString().slice(0, 10);
};
// Gaps in a DENSE (daily, not downsampled) date series: [{from, to, sessions}] — the client twin of
// freshness.gaps_in for series that arrive without an envelope (a stock's price1y).
export function seriesGaps(dates, maxSessions = GAP_SESSIONS) {
  const out = []; let prev = null;
  for (const d of dates || []) {
    if (!d) continue;
    if (prev) { const n = sessionsBetween(prev, d); if (n > maxSessions) out.push({ from: prev, to: d, sessions: n }); }
    prev = d;
  }
  return out;
}
// Break a line across the envelope's gaps: returns the SVG path of `pts` (each {t,…}) with a fresh
// "M" after every gap boundary so the stopped stretch draws as a hole, not as a calm flat line (W-12).
// `gaps` = meta.gaps ([{from, to}] as dates); `t` is a fractional year (dates.js yearFrac).
export function gapBreaks(gaps) {
  return (gaps || []).map((g) => ({ a: yearFrac(g.from), b: yearFrac(g.to), from: g.from, to: g.to })).filter((g) => Number.isFinite(g.a) && Number.isFinite(g.b));
}
const spansGap = (q, p, breaks) => breaks.some((g) => q.t <= g.a + 1e-9 && p.t >= g.b - 1e-9);
export function pathAcrossGaps(pts, breaks, xOf, yOf) {
  let d = "", pen = false;
  for (let i = 0; i < pts.length; i++) {
    const p = pts[i];
    if (i > 0 && spansGap(pts[i - 1], p, breaks)) pen = false;   // this segment spans a gap
    d += `${pen ? " L" : (d ? " M" : "M")} ${xOf(p).toFixed(1)} ${yOf(p).toFixed(1)}`;
    pen = true;
  }
  return d;
}
// The DASHED bridge over each gap (last point before → first point after): drawn thin and dashed so
// the eye sees "no data here", never a solid line reading as calm. Returns one SVG path string.
export function gapSegments(pts, breaks, xOf, yOf) {
  let d = "";
  for (let i = 1; i < pts.length; i++) {
    const q = pts[i - 1], p = pts[i];
    if (spansGap(q, p, breaks)) d += `M ${xOf(q).toFixed(1)} ${yOf(q).toFixed(1)} L ${xOf(p).toFixed(1)} ${yOf(p).toFixed(1)} `;
  }
  return d.trim();
}
// The dashed gap bridge as an element (inside the host <svg>); null when there is no gap.
export function GapPath({ d, color = "var(--text-3)", testid = "chart-gap-segment" }) {
  if (!d) return null;
  return <path d={d} fill="none" stroke={color} strokeWidth="1.2" strokeDasharray="3 4" strokeOpacity="0.9"
    vectorEffect="non-scaling-stroke" data-testid={testid} />;
}
// "no data 2026-06-12–2026-07-03" chip(s) — the caveat next to the chart, one per gap (max 3 listed).
export function GapNote({ gaps, testid = "chart-gap-note" }) {
  if (!gaps?.length) return null;
  const shown = gaps.slice(0, 3);
  return (
    <span className="chip chip--fresh chip--fresh-sm chip--stale" data-testid={testid}
      title={gaps.map((g) => `no data ${g.from} – ${g.to}${g.sessions ? ` (${g.sessions} sessions)` : ""}`).join(" · ")}>
      {shown.map((g) => `no data ${g.from}–${g.to}`).join(" · ")}{gaps.length > 3 ? ` · +${gaps.length - 3} more` : ""}
    </span>
  );
}

// The end marker: a ring + "as of" label at the series' last point, so the eye reads WHERE the data
// stops instead of assuming the right edge is today. Render inside the host <svg>.
export function EndMarker({ x, y, meta, color = "var(--accent)", up = true }) {
  if (!meta?.as_of || !Number.isFinite(x) || !Number.isFinite(y)) return null;
  const stale = !!meta.stale;
  return (
    <g data-testid="chart-end-marker" data-stale={stale ? "1" : "0"}>
      <circle cx={x} cy={y} r="4" fill="var(--panel)" stroke={stale ? "var(--amber)" : color} strokeWidth="2" vectorEffect="non-scaling-stroke" />
      <text x={x} y={up ? y - 9 : y + 16} textAnchor="end" fontSize="10.5" fontFamily="var(--font-mono)"
        fill={stale ? "var(--amber)" : "var(--text-2)"}>{stale ? "⚠ " : ""}as of {meta.as_of}</text>
    </g>
  );
}
